import { useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import {
  pauseIntegration,
  unpauseIntegration,
  PauseVariables,
  UnPauseVariables,
  ResponseStatus,
} from 'data/requests/pauseIntegration';
import { CDError } from 'utils/error';
import { Brand } from 'model/brand';

const useBrandActions = () => {
  const queryCache = useQueryClient();
  const { mutateAsync: pauseIntegrationMutation, isLoading: pausedLoading } =
    useMutation<ResponseStatus, CDError, PauseVariables>(pauseIntegration);
  const {
    mutateAsync: unpauseIntegrationMutation,
    isLoading: unpausedLoading,
  } = useMutation<ResponseStatus, CDError, UnPauseVariables>(
    unpauseIntegration,
  );

  const updateState = useCallback(
    async (brandId, integrationId) => {
      const prevBrands = queryCache.getQueryData<Brand[]>('brands') || [];

      const brandsUpdated = prevBrands.map((prevBrand) => {
        if (prevBrand.id === brandId) {
          return {
            ...prevBrand,
            integrations: prevBrand.integrations.map((prevIntegration) => {
              if (prevIntegration.id === integrationId) {
                return {
                  ...prevIntegration,
                  isActive: false,
                  closureReason: 'Restaurant Closure',
                };
              }
              return prevIntegration;
            }),
          };
        }
        return prevBrand;
      });

      queryCache.setQueryData('brands', brandsUpdated);
    },
    [queryCache],
  );

  const pause = useCallback(
    async (data: PauseVariables) => {
      const response = await pauseIntegrationMutation(data);

      const prevBrands = queryCache.getQueryData<Brand[]>('brands') || [];

      const brandsUpdated = prevBrands.map((prevBrand) => {
        if (prevBrand.id === data.brandId || data.brandId == null) {
          return {
            ...prevBrand,
            integrations: prevBrand.integrations.map((prevIntegration) => {
              if (
                prevIntegration.id === data.integrationId ||
                data.integrationId == null
              ) {
                return {
                  ...prevIntegration,
                  isActive: false,
                  isBusy: true,
                  closureReason: 'Restaurant Closure',
                };
              }
              return prevIntegration;
            }),
          };
        }
        return prevBrand;
      });

      queryCache.setQueryData('brands', brandsUpdated);
      queryCache.invalidateQueries(['brands']);

      return response;
    },
    [pauseIntegrationMutation, queryCache],
  );

  const unpause = useCallback(
    async (data: UnPauseVariables) => {
      const response = await unpauseIntegrationMutation(data);

      const prevBrands = queryCache.getQueryData<Brand[]>('brands') || [];

      const brandsUpdated = prevBrands.map((prevBrand) => {
        if (prevBrand.id === data.brandId || data.brandId == null) {
          return {
            ...prevBrand,
            integrations: prevBrand.integrations.map((prevIntegration) => {
              if (
                prevIntegration.id === data.integrationId ||
                data.integrationId == null
              ) {
                return {
                  ...prevIntegration,
                  isActive: true,
                  isBusy: false,
                  busyUntil: null,
                  closureReason: 'Restaurant Open',
                };
              }
              return prevIntegration;
            }),
          };
        }
        return prevBrand;
      });

      queryCache.setQueryData('brands', brandsUpdated);
      queryCache.invalidateQueries(['brands']);

      return response;
    },

    [unpauseIntegrationMutation, queryCache],
  );

  return {
    isLoading: pausedLoading || unpausedLoading,
    pause,
    unpause,
    updateState,
  };
};

export default useBrandActions;
